// src/render/map/polygon-canopy.ts — per-tree polygon canopy generation + fade state.
//
// Invariants:
//   - Zero runtime Phaser dependency — pure TS computation only.
//   - Uses a caller-supplied seeded RNG (never Math.random) so tree shapes are
//     deterministic per position and stable across page reloads.
//   - Fully unit-testable without a Phaser context.
//
// Sim/render boundary: render-only. Never imported by src/sim/**.

import type { TreeObject, Vec2 } from '../../shared/types/map';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Leafy-green canopy fill colour matching the tileset tree-canopy (#6a7a3e). */
export const CANOPY_COLOR = 0x6a7a3e;

/** Alpha the canopy fades to when the player walks under it (match existing tile fade). */
export const CANOPY_FADE_ALPHA = 0.3;

/** Number of canopy blob points drawn per tree (inclusive range; randomly chosen). */
export const CANOPY_MIN_POINTS = 10;
export const CANOPY_MAX_POINTS = 14;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Minimal render-object interface required for per-tree alpha fade.
 *  `Phaser.GameObjects.Graphics` satisfies this structurally. */
export interface AlphaTarget {
  setAlpha: (alpha: number) => unknown;
}

/**
 * Per-tree state for the polygon canopy walk-under fade.
 * Mirrors FadeGroup from canopy-fade.ts but operates on a single Graphics
 * object rather than a set of tiles.
 */
export interface PolygonCanopyGroup {
  /** The Graphics object containing the drawn canopy blob. */
  graphics: AlphaTarget;
  /** Source tree ellipse used for proximity (point-in-ellipse) check. */
  tree: TreeObject;
  /** Mutable alpha counter tweened by Phaser; applied via onUpdate. */
  tweenCounter: { alpha: number };
  state: 'visible' | 'fading-in' | 'transparent' | 'fading-out';
}

// ─── pointInPolygon ──────────────────────────────────────────────────────────

/**
 * Ray-casting point-in-polygon test (Jordan curve theorem).
 *
 * Returns true when world-pixel point (px, py) is inside the closed polygon
 * defined by `verts`. Points exactly on an edge are implementation-defined —
 * inconsequential for the roof proximity use-case (sub-pixel precision).
 *
 * Used by PolygonRoofGroup proximity check in GameScene to trigger walk-under
 * fade when the player enters a roof polygon.
 */
export function pointInPolygon(px: number, py: number, verts: ReadonlyArray<Vec2>): boolean {
  let inside = false;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = verts[i]!.x;
    const yi = verts[i]!.y;
    const xj = verts[j]!.x;
    const yj = verts[j]!.y;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// ─── PolygonRoofGroup ─────────────────────────────────────────────────────────

/**
 * Per-roof state for the polygon roof walk-under fade.
 * Mirrors PolygonCanopyGroup but uses a polygon vertex list for the proximity
 * check (pointInPolygon) rather than an ellipse (isInsideEllipse).
 *
 * Roofs render at DEPTH_CANOPY (30) — above characters — and fade to ~0.3
 * over 150 ms when the player enters the polygon, restoring over 400 ms on exit.
 */
export interface PolygonRoofGroup {
  /** The Graphics object containing the drawn roof polygon. */
  graphics: AlphaTarget;
  /** Absolute world-pixel vertices used for pointInPolygon proximity check. */
  vertices: Vec2[];
  /** Mutable alpha counter tweened by Phaser; applied via onUpdate. */
  tweenCounter: { alpha: number };
  state: 'visible' | 'fading-in' | 'transparent' | 'fading-out';
}

// ─── generateBlobVertices ─────────────────────────────────────────────────────

/**
 * Generate an irregular "natural blob" polygon around a tree ellipse.
 *
 * Algorithm: distribute `numPoints` evenly around the ellipse (equal angular
 * spacing), then independently perturb each point's angle (±10% of the angular
 * step) and radius (±20% of the semi-axis) using the caller-supplied RNG.
 * The result looks like an organic crown rather than a perfect ellipse.
 *
 * Determinism contract: for the same (cx, cy, rx, ry, numPoints) and RNG
 * seeded identically, this function returns the same vertices on every call.
 * The caller (GameScene) seeds the RNG from the tree's pixel centre so each
 * tree's shape is stable across page reloads and unique between trees.
 *
 * @param cx       World-pixel centre X of the ellipse.
 * @param cy       World-pixel centre Y of the ellipse.
 * @param rx       Horizontal semi-axis in world pixels.
 * @param ry       Vertical semi-axis in world pixels.
 * @param numPoints Number of blob vertices (CANOPY_MIN_POINTS..CANOPY_MAX_POINTS).
 * @param rng      Seeded random function returning a value in [0, 1).
 */
export function generateBlobVertices(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  numPoints: number,
  rng: () => number,
): Vec2[] {
  const vertices: Vec2[] = [];
  const angularStep = (2 * Math.PI) / numPoints;
  for (let i = 0; i < numPoints; i++) {
    // Angular jitter: ±10% of the full angular step between vertices.
    const baseAngle = angularStep * i;
    const angleJitter = (rng() * 2 - 1) * angularStep * 0.1;
    const angle = baseAngle + angleJitter;
    // Radius perturbation: ±20% (so each point is 80–120% of the semi-axis).
    const radiusScale = 1 + (rng() * 2 - 1) * 0.2;
    vertices.push({
      x: cx + Math.cos(angle) * rx * radiusScale,
      y: cy + Math.sin(angle) * ry * radiusScale,
    });
  }
  return vertices;
}

// ─── isInsideEllipse ─────────────────────────────────────────────────────────

/**
 * Return true when world-pixel point (px, py) lies inside the ellipse
 * described by centre (cx, cy) and semi-axes rx/ry.
 *
 * Uses the standard normalised-quadrant test:
 *   (dx/rx)² + (dy/ry)² ≤ 1
 *
 * Points exactly on the boundary are considered inside (≤). A zero rx/ry
 * is never inside (defensive: no division if rx or ry is 0).
 */
export function isInsideEllipse(
  px: number,
  py: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): boolean {
  if (rx <= 0 || ry <= 0) return false;
  const dx = (px - cx) / rx;
  const dy = (py - cy) / ry;
  return dx * dx + dy * dy <= 1;
}
